/**
 * Kunjaree — apply Shopify discount codes to cart (Admin-created codes).
 * Redirect via /discount/{code} for checkout; AJAX apply for add-to-cart (no page reload).
 */
(function(global) {
  'use strict';

  var OPEN_MINI_CART_KEY = 'kunjaree_open_mini_cart';
  var MINI_CART_SECTIONS = ['mini-cart-drawer', 'cart-notification-count'];

  function normalizeCode(code) {
    return String(code || '').trim();
  }

  function codesMatch(a, b) {
    return normalizeCode(a).toUpperCase() === normalizeCode(b).toUpperCase();
  }

  function getPromoConfig() {
    var cfg = global.kunjareePromo || {};
    return {
      code: normalizeCode(cfg.code || 'FIRST10'),
      percent: String(cfg.percent || '10'),
      autoApply: cfg.autoApply !== false && cfg.autoApply !== 'false',
      skipForReturning: cfg.skipForReturning !== false && cfg.skipForReturning !== 'false',
      ordersCount: parseInt(cfg.ordersCount, 10) || 0
    };
  }

  function applyViaRedirect(code, redirectPath) {
    var normalized = normalizeCode(code);
    if (!normalized) return Promise.reject(new Error('Enter a discount code'));
    var redirect = redirectPath || (global.location.pathname + global.location.search);
    global.location.href =
      '/discount/' + encodeURIComponent(normalized) +
      '?redirect=' + encodeURIComponent(redirect);
    return Promise.resolve();
  }

  /**
   * Apply discount without full-page navigation (sets discount cookie via redirect: manual).
   */
  function applyViaDiscountEndpoint(code) {
    var normalized = normalizeCode(code);
    if (!normalized) return Promise.reject(new Error('Enter a discount code'));

    var discountUrl =
      '/discount/' + encodeURIComponent(normalized) +
      '?redirect=' + encodeURIComponent(global.location.pathname || '/');

    return fetch(discountUrl, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual'
    }).then(function(res) {
      if (
        res.type === 'opaqueredirect' ||
        res.status === 0 ||
        (res.status >= 300 && res.status < 400) ||
        res.ok
      ) {
        return fetchCart();
      }
      return Promise.reject(new Error('Could not apply discount code'));
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function(res) {
      return res.json();
    });
  }

  function getDiscountInfo(cart) {
    if (!cart) return { applied: false, code: '', amount: 0, applications: [] };
    var apps = cart.cart_level_discount_applications || [];
    var amount = (cart.total_discount || 0) / 100;
    var code = '';
    if (apps.length) {
      code = apps[0].title || apps[0].code || '';
    }
    return {
      applied: apps.length > 0 || amount > 0,
      code: code,
      amount: amount,
      applications: apps
    };
  }

  function isCodeAlreadyApplied(cart, code) {
    if (!cart || !code) return false;
    var info = getDiscountInfo(cart);
    if (!info.applied) return false;
    if (codesMatch(info.code, code)) return true;
    return info.applications.some(function(app) {
      return codesMatch(app.title, code) || codesMatch(app.code, code);
    });
  }

  function shouldAutoApply(cart) {
    var cfg = getPromoConfig();
    if (!cfg.autoApply || !cfg.code) return false;
    if (cfg.skipForReturning && cfg.ordersCount > 0) return false;
    if (!cart || !cart.item_count) return false;
    if (getDiscountInfo(cart).applied) return false;
    return true;
  }

  function refreshCartPageSections() {
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    if (!itemsEl || !footerEl || !itemsEl.dataset.id || !footerEl.dataset.id) {
      return Promise.resolve(null);
    }

    var sections = [itemsEl.dataset.id, footerEl.dataset.id].join(',');
    var url = (global.location.pathname || '/cart') + '?sections=' + sections;

    return fetch(url, { credentials: 'same-origin' }).then(function(res) {
      return res.json();
    }).then(function(sectionsHtml) {
      return fetchCart().then(function(cart) {
      var cartItems = document.querySelector('cart-items');
      var payload = {
        item_count: cart.item_count,
        sections: sectionsHtml
      };
      if (cartItems && typeof cartItems.renderContents === 'function') {
        cartItems.renderContents(payload);
      } else {
        ['main-cart-items', 'main-cart-footer'].forEach(function(id, index) {
          var sectionId = index === 0 ? itemsEl.dataset.id : footerEl.dataset.id;
          var html = sectionsHtml[sectionId];
          if (!html) return;
          var container = document.getElementById(id);
          var target = container && container.closest('.js-contents') || (container && container.querySelector('.js-contents'));
          if (!target && id === 'main-cart-footer') {
            target = document.querySelector('#main-cart-footer .js-contents');
          }
          if (!target && id === 'main-cart-items') {
            target = document.querySelector('cart-items .js-contents') || document.querySelector('#main-cart-items')?.closest('.js-contents');
          }
          if (target) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var inner = doc.querySelector('.js-contents') || doc.querySelector('.shopify-section');
            if (inner) target.innerHTML = inner.innerHTML;
          }
        });
        if (cartItems && typeof cartItems.updateCartPageSubtitle === 'function') {
          cartItems.updateCartPageSubtitle(cart.item_count);
        } else {
          var subtitle = document.getElementById('kunjaree-cart-page-subtitle');
          if (subtitle) {
            var count = cart.item_count || 0;
            if (count === 0) {
              subtitle.textContent = 'Your selection is empty — explore our collection below';
            } else {
              subtitle.textContent = count + (count === 1 ? ' item' : ' items') + ' · Review your items before checkout';
            }
          }
        }
      }
      try {
        global.dispatchEvent(new CustomEvent('kunjaree:cart-sections-updated'));
      } catch (err) {}
      return sectionsHtml;
      });
    });
  }

  function autoApplyPromoOnCartPage() {
    if (!global.document.body.classList.contains('template-cart')) return Promise.resolve(null);

    return autoApplyPromo({ useRedirect: false }).then(function(result) {
      if (result && result.applied) {
        return refreshCartPageSections();
      }
      return result;
    });
  }

  /**
   * Auto-apply configured promo if eligible.
   * @param {Object} options
   * @param {string} [options.redirectPath]
   * @param {boolean} [options.openMiniCart] - open mini-cart after redirect reload (checkout only)
   * @param {boolean} [options.useRedirect=true] - false = AJAX apply (add-to-cart flow)
   */
  function autoApplyPromo(options) {
    options = options || {};
    var redirectPath = options.redirectPath || (global.location.pathname + global.location.search);
    var openMiniCart = !!options.openMiniCart;
    var useRedirect = options.useRedirect !== false;
    var cfg = getPromoConfig();

    return fetchCart().then(function(cart) {
      var info = getDiscountInfo(cart);
      if (info.applied) {
        return { skipped: true, alreadyApplied: true, cart: cart };
      }
      if (!shouldAutoApply(cart)) {
        return { skipped: true, alreadyApplied: false, cart: cart };
      }

      if (useRedirect) {
        if (openMiniCart) {
          try {
            sessionStorage.setItem(OPEN_MINI_CART_KEY, '1');
          } catch (err) {}
        }
        applyViaRedirect(cfg.code, redirectPath);
        return { redirected: true };
      }

      return applyViaDiscountEndpoint(cfg.code)
        .then(function(updatedCart) {
          if (!isCodeAlreadyApplied(updatedCart, cfg.code)) {
            return { skipped: true, applied: false, cart: updatedCart };
          }
          return refreshMiniCartSections().then(function(sections) {
            return {
              applied: true,
              cart: updatedCart,
              sections: sections
            };
          });
        })
        .catch(function() {
          return { skipped: true, applied: false, cart: cart };
        });
    });
  }

  function refreshMiniCartSections() {
    var path = global.location.pathname || '/';
    var url = path + '?sections=' + MINI_CART_SECTIONS.join(',');
    return fetch(url, { credentials: 'same-origin' }).then(function(res) {
      return res.json();
    });
  }

  function openMiniCartIfFlagged() {
    var shouldOpen = false;
    try {
      shouldOpen = sessionStorage.getItem(OPEN_MINI_CART_KEY) === '1';
      if (shouldOpen) sessionStorage.removeItem(OPEN_MINI_CART_KEY);
    } catch (err) {}
    if (!shouldOpen) return;

    global.setTimeout(function() {
      var drawer = document.getElementById('mini-cart-drawer');
      if (!drawer) return;
      drawer.classList.add('animate', 'active');
      document.body.classList.add('added__overlay');
    }, 200);
  }

  function bindApplyForm(options) {
    options = options || {};
    var input = options.input;
    var button = options.button;
    var errorEl = options.errorEl;
    var successEl = options.successEl;
    var onSuccess = options.onSuccess;
    var redirectPath = options.redirectPath || global.location.pathname + global.location.search;

    if (!input || !button) return;

    function showError(msg) {
      if (errorEl) {
        errorEl.textContent = msg || '';
        errorEl.style.display = msg ? 'block' : 'none';
      }
    }

    function showSuccess(msg) {
      if (successEl) {
        successEl.textContent = msg || '';
        successEl.style.display = msg ? 'block' : 'none';
      }
    }

    function runApply() {
      var code = normalizeCode(input.value);
      if (!code) {
        showError('Enter a discount code');
        return;
      }
      showError('');
      button.disabled = true;
      var prevLabel = button.textContent;
      button.textContent = button.dataset.loadingText || 'Applying…';

      var done = function() {
        button.disabled = false;
        button.textContent = prevLabel;
      };

      fetchCart()
        .then(function(cart) {
          if (isCodeAlreadyApplied(cart, code)) {
            showSuccess(code + ' is already applied.');
            if (typeof onSuccess === 'function') onSuccess(cart);
            done();
            return null;
          }
          return applyViaRedirect(code, redirectPath);
        })
        .catch(function(err) {
          showError(err.message || 'Could not apply discount code');
          done();
        });
    }

    button.addEventListener('click', function(e) {
      e.preventDefault();
      runApply();
    });

    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        runApply();
      }
    });
  }

  global.KunjareeCartDiscount = {
    applyViaRedirect: applyViaRedirect,
    applyViaDiscountEndpoint: applyViaDiscountEndpoint,
    fetchCart: fetchCart,
    getDiscountInfo: getDiscountInfo,
    isCodeAlreadyApplied: isCodeAlreadyApplied,
    getPromoConfig: getPromoConfig,
    shouldAutoApply: shouldAutoApply,
    autoApplyPromo: autoApplyPromo,
    autoApplyPromoOnCartPage: autoApplyPromoOnCartPage,
    refreshMiniCartSections: refreshMiniCartSections,
    refreshCartPageSections: refreshCartPageSections,
    bindApplyForm: bindApplyForm
  };

  document.addEventListener('DOMContentLoaded', function() {
    openMiniCartIfFlagged();

    var drawerInput = document.getElementById('coupon_code_drawer');
    var drawerBtn = document.getElementById('coupon_apply_drawer');
    if (drawerInput && drawerBtn) {
      bindApplyForm({
        input: drawerInput,
        button: drawerBtn,
        errorEl: document.getElementById('coupon_error_drawer'),
        redirectPath: global.location.pathname
      });
    }
  });
})(window);
